import i18n from 'helpers/i18n';
import messages from './messages';
import useAsync from 'hooks/useAsync';
import { useFormikContext } from 'formik';
import { getHierarchy } from 'actions/accounts';
import { useCallback, useEffect, useMemo } from 'react';
import HierarchyDropdown from 'components/Reports/Observation/HierarchyDropdown';
import { Text } from '@teachstonellc/component-library';

type FormValues = {
  account_id?: number;
  node_id?: number;
};

type Node = {
  id: number;
  name: string;
  type: string;
  deleted: boolean;
  children: Node[];
};

type HierarchyData = {
  label: string;
  value: number;
  children: HierarchyData[];
  checked: boolean;
};

type CenterSelectProps = {
  readOnly?: boolean;
  savedCenter?: {
    id: number;
    name: string;
  };
};

function buildHierarchyData(nodes: Node[], nodeId?: number): HierarchyData[] {
  const nonClassroomNodes: HierarchyData[] = [];

  nodes.forEach(node => {
    if (node.type === 'Classroom') {
      return;
    }

    nonClassroomNodes.push({
      checked: nodeId === node.id,
      label: node.deleted ? `${node.name} (archived)` : node.name,
      value: node.id,
      children: buildHierarchyData(node.children, nodeId)
    });
  });

  return nonClassroomNodes;
}

export function CenterSelect({
  readOnly = false,
  savedCenter
}: CenterSelectProps) {
  const formik = useFormikContext<FormValues>();
  const accountId = formik.values.account_id || 0;
  const nodeId = formik.values.node_id || 0;
  const { run, data } = useAsync({ data: {} });

  useEffect(() => {
    if (!accountId || readOnly) {
      return;
    }

    run(getHierarchy(accountId));
  }, [run, accountId, readOnly]);

  const hierarchy = useMemo(
    () => buildHierarchyData(data.hierarchy?.children || [], nodeId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );
  const readOnlyHierarchy = useMemo(
    () =>
      savedCenter
        ? [
            {
              checked: true,
              label: savedCenter.name,
              value: savedCenter.id,
              children: []
            }
          ]
        : [],
    [savedCenter]
  );

  const handleChange = useCallback((_: any, tags: any) => {
    const nodes = tags.map((node: HierarchyData) => node.value);
    formik.setFieldValue('node_id', nodes[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!accountId) {
    return null;
  }

  return (
    <div>
      <Text
        as="label"
        variant="label1"
        className="block mb-2 text-ds-text-secondary font-medium"
      >
        {i18n.ft(messages.center)}
      </Text>
      <HierarchyDropdown
        mode="radioSelect"
        data={readOnly ? readOnlyHierarchy : hierarchy}
        onChange={handleChange}
        disabled={readOnly}
      />
    </div>
  );
}
